/**
 * PaywallModal — Premium subscription paywall for LOGOS HUMANO
 * 
 * Displays when a free user tries to access a premium feature.
 * Uses StoreKit 2 via LOGOSSubscriptionPlugin for purchases.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONTS } from '../domain';

const fonts = FONTS;

const PREMIUM_FEATURE_KEYS = [
  'engine', 'montecarlo', 'biometrics', 'trajectory',
  'voice', 'emergence', 'protocols', 'alimento',
];

const FREE_FEATURE_KEYS = [
  'sliders', 'semaphore', 'radar', 'guide', 'checkin',
];

/**
 * Full-screen paywall modal shown when user attempts to access a premium tab.
 * 
 * @param {Object} props
 * @param {boolean} props.visible - Whether to show the modal
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onPurchase - Purchase handler (async, returns boolean)
 * @param {Function} props.onRestore - Restore handler (async, returns boolean)
 * @param {boolean} props.purchasing - Whether a purchase is in progress
 * @param {Object[]} props.products - Available products from App Store
 * @param {string|null} props.error - Error message
 * @param {boolean} props.isNative - Whether running on native iOS
 */
export default function PaywallModal({
  visible,
  onClose,
  onPurchase,
  onRestore,
  purchasing = false,
  products = [],
  error = null,
  isNative = false,
}) {
  const { t } = useTranslation();
  const [restoring, setRestoring] = useState(false);

  if (!visible) return null;

  const product = products[0]; // Monthly subscription

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const restored = await onRestore();
      if (restored) onClose();
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      animation: "fadeInUp 0.4s ease",
    }}>
      <div style={{
        width: "92%", maxWidth: 520, maxHeight: "90vh", overflow: "auto",
        background: `linear-gradient(145deg, ${COLORS.surface}, ${COLORS.bg})`,
        border: `1px solid ${COLORS.logos}40`, borderRadius: 20,
        padding: "28px 24px", position: "relative",
        boxShadow: `0 0 80px ${COLORS.logos}20, inset 0 0 40px ${COLORS.logos}05`,
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: "absolute", top: 12, right: 16,
          background: "none", border: "none", cursor: "pointer",
          fontSize: 18, color: COLORS.textMuted, padding: 4,
        }}>
          ✕
        </button>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            fontSize: 48, marginBottom: 8,
            animation: "breathe 3s ease infinite",
            textShadow: `0 0 40px ${COLORS.logos}60`,
          }}>
            ✦
          </div>
          <h2 style={{
            fontSize: 20, fontWeight: 700, margin: "0 0 6px 0",
            fontFamily: fonts.display,
            background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.logos})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            {t('subscription.premiumTitle')}
          </h2>
          <p style={{
            fontSize: 11, fontFamily: fonts.mono, color: COLORS.textMuted,
            margin: 0, lineHeight: 1.5,
          }}>
            {t('subscription.premiumSubtitle')}
          </p>
        </div>

        {/* Premium Features */}
        <div style={{
          marginBottom: 16, padding: 14,
          background: `${COLORS.logos}08`, borderRadius: 12,
          border: `1px solid ${COLORS.logos}25`,
        }}>
          <div style={{
            fontSize: 9, fontFamily: fonts.mono, fontWeight: 600,
            color: COLORS.logos, marginBottom: 10, letterSpacing: 1.5,
          }}>
            {t('subscription.premiumBadge')}
          </div>
          {PREMIUM_FEATURE_KEYS.map(key => (
            <div key={key} style={{
              fontSize: 10, fontFamily: fonts.mono, color: COLORS.text,
              lineHeight: 1.6, padding: "2px 0",
            }}>
              <span style={{ color: COLORS.logos, marginRight: 6 }}>✦</span>
              {t(`subscription.premiumFeatures.${key}`)}
            </div>
          ))}
        </div>

        {/* Free Features */}
        <div style={{
          marginBottom: 20, padding: 14,
          background: `${COLORS.textMuted}08`, borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{
            fontSize: 9, fontFamily: fonts.mono, fontWeight: 600,
            color: COLORS.textMuted, marginBottom: 10, letterSpacing: 1.5,
          }}>
            {t('subscription.freeBadge')}
          </div>
          {FREE_FEATURE_KEYS.map(key => (
            <div key={key} style={{
              fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim,
              lineHeight: 1.6, padding: "2px 0",
            }}>
              <span style={{ color: COLORS.textMuted, marginRight: 6 }}>✓</span>
              {t(`subscription.freeFeatures.${key}`)}
            </div>
          ))}
        </div>

        {/* Price & Subscribe */}
        {isNative ? (
          <>
            {product && (
              <div style={{ textAlign: "center", marginBottom: 12 }}>
                <span style={{
                  fontSize: 28, fontWeight: 700, fontFamily: fonts.mono,
                  color: COLORS.text,
                }}>
                  {product.displayPrice}
                </span>
                <span style={{
                  fontSize: 11, fontFamily: fonts.mono, color: COLORS.textMuted,
                  marginLeft: 4,
                }}>
                  / {t('subscription.monthlyLabel').toLowerCase()}
                </span>
              </div>
            )}

            <button
              onClick={onPurchase}
              disabled={purchasing || restoring}
              style={{
                width: "100%", padding: "14px 28px", borderRadius: 12,
                border: "none", cursor: purchasing ? "wait" : "pointer",
                fontFamily: fonts.mono, fontSize: 13, fontWeight: 700,
                letterSpacing: 1,
                background: `linear-gradient(135deg, ${COLORS.logos}, ${COLORS.viability})`,
                color: "#fff",
                boxShadow: `0 4px 24px ${COLORS.logos}50`,
                opacity: purchasing || restoring ? 0.6 : 1,
                transition: "all 0.3s",
              }}
            >
              {purchasing ? t('subscription.subscribing') : t('subscription.subscribeButton')}
            </button>

            {/* Restore */}
            <button
              onClick={handleRestore}
              disabled={purchasing || restoring}
              style={{
                width: "100%", marginTop: 10, padding: "10px 20px",
                borderRadius: 10, cursor: "pointer",
                background: "transparent",
                border: `1px solid ${COLORS.border}`,
                fontFamily: fonts.mono, fontSize: 10, color: COLORS.textMuted,
                opacity: purchasing || restoring ? 0.5 : 1,
              }}
            >
              {restoring ? t('subscription.restoring') : t('subscription.restoreButton')}
            </button>

            {/* Error */}
            {error && (
              <div style={{
                marginTop: 10, padding: 10, borderRadius: 8,
                background: "#ef444410", border: "1px solid #ef444430",
                fontSize: 9, fontFamily: fonts.mono, color: "#ef4444",
                textAlign: "center",
              }}>
                {t('subscription.error')}
              </div>
            )}

            {/* Terms */}
            <div style={{
              marginTop: 14, fontSize: 8, fontFamily: fonts.mono,
              color: COLORS.textMuted, lineHeight: 1.6, textAlign: "center",
              opacity: 0.7,
            }}>
              {t('subscription.termsNote')}
            </div>
          </>
        ) : (
          <div style={{
            textAlign: "center", padding: 16, fontSize: 11,
            fontFamily: fonts.mono, color: COLORS.textMuted,
          }}>
            {t('subscription.notAvailable')}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Inline premium lock overlay — shown on top of premium tab content.
 * Smaller than the full modal, used as a tab-level gate.
 */
export function PremiumLockBanner({ onUpgrade, t: tProp }) {
  const { t: tHook } = useTranslation();
  const t = tProp || tHook;

  return (
    <div style={{
      textAlign: "center", padding: "32px 20px",
      background: `linear-gradient(145deg, ${COLORS.surface}, ${COLORS.bg})`,
      borderRadius: 16, border: `1px solid ${COLORS.logos}30`,
      margin: "0 auto", maxWidth: 500,
    }}>
      <div style={{
        fontSize: 36, marginBottom: 12,
        textShadow: `0 0 30px ${COLORS.logos}40`,
      }}>
        🔒
      </div>
      <div style={{
        fontSize: 14, fontFamily: fonts.mono, fontWeight: 600,
        color: COLORS.text, marginBottom: 6,
      }}>
        {t('subscription.paywallTitle')}
      </div>
      <div style={{
        fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted,
        lineHeight: 1.6, marginBottom: 16,
      }}>
        {t('subscription.paywallBody')}
      </div>
      <button onClick={onUpgrade} style={{
        padding: "12px 32px", borderRadius: 10, border: "none",
        cursor: "pointer", fontFamily: fonts.mono, fontSize: 11,
        fontWeight: 600, letterSpacing: 1,
        background: `linear-gradient(135deg, ${COLORS.logos}, ${COLORS.viability})`,
        color: "#fff", boxShadow: `0 4px 20px ${COLORS.logos}40`,
      }}>
        {t('subscription.subscribeButton')}
      </button>
    </div>
  );
}
